import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { AuthService } from '../../../services/auth/auth.service';
import { FrontendLoggerService } from '../../../services/frontend-logger.service';
import { LanguageService } from '../../../services/language/language.service';
import { ThemeService } from '../../../services/theme/theme.service';
import { UserService } from '../../../services/user.service';
import { AuthWizardTimezoneCatalog, WizardTimezone } from '../auth-wizard-timezone.catalog';
import type { ValidateTokenResponse } from './invite-wizard.component';

export interface InviteInitializationPort {
  setInviteContext(token: string, email: string): void;
  reject(errorKey: string): void;
  accept(
    invite: ValidateTokenResponse,
    form: FormGroup,
    timezones: WizardTimezone[]
  ): void;
}

export class InviteFlowInitializer {
  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly formBuilder: FormBuilder,
    private readonly authService: AuthService,
    private readonly userService: UserService,
    private readonly themeService: ThemeService,
    private readonly languageService: LanguageService,
    private readonly logger: FrontendLoggerService,
    private readonly timezoneCatalog: AuthWizardTimezoneCatalog
  ) {}

  async initialize(port: InviteInitializationPort): Promise<void> {
    const routeToken = (this.route.snapshot.queryParamMap.get('token') || '').trim();
    const routeEmail = (this.route.snapshot.queryParamMap.get('email') || '').trim();
    const pending = this.authService.getPendingInviteContext();
    const token = routeToken || pending?.token || '';
    const email = routeEmail || pending?.email || '';
    port.setInviteContext(token, email);

    if (routeToken && routeEmail) {
      this.authService.setPendingInviteContext(routeToken, routeEmail);
      void this.router.navigate([], {
        relativeTo: this.route,
        replaceUrl: true,
        queryParams: {},
      });
    }

    if (!token || !email) {
      this.authService.clearPendingInviteContext();
      port.reject('invite.missing_params');
      return;
    }

    this.authService.setPendingInviteContext(token, email);
    this.userService.validateInviteToken(token, email).subscribe({
      next: async (invite) => this.acceptInvite(invite, port),
      error: (error) => this.rejectInvite(error, port),
    });
  }

  private async acceptInvite(
    invite: ValidateTokenResponse,
    port: InviteInitializationPort
  ): Promise<void> {
    const theme = await firstValueFrom(this.themeService.currentTheme$);
    const language = await firstValueFrom(this.languageService.currentLang$);
    const form = this.formBuilder.group({
      firstName: ['', Validators.required],
      secondName: ['', Validators.required],
      jobTitle: ['', Validators.required],
      company: [invite?.companyName || '', Validators.required],
      timezone: ['', Validators.required],
      theme: [theme],
      language: [language],
      notifications: [true],
      dataCollection: [true],
      password: ['', [Validators.required, Validators.minLength(8)]],
      confirmPassword: ['', Validators.required],
    });
    const timezones = this.timezoneCatalog.build();
    const guessedTimezone = this.timezoneCatalog.guess(timezones);
    if (guessedTimezone) form.patchValue({ timezone: guessedTimezone });
    port.accept(invite, form, timezones);
  }

  private rejectInvite(error: any, port: InviteInitializationPort): void {
    if (error?.status === 401 || error?.status === 404) {
      this.authService.clearPendingInviteContext();
    }
    const errorKey = error?.status === 401
      ? 'invite.invalid_or_expired'
      : error?.status === 404
        ? 'invite.not_found'
        : 'invite.server_error';
    port.reject(errorKey);
    this.logger.error('[InviteWizard] validateInvite error: ' + JSON.stringify(error));
  }
}
